import React, { useCallback, useState, useEffect, useRef } from "react";
import { useGetAllCollectionsLazyQuery } from "@api/graphql-codegen";
import styles from "./SuperCollections.module.scss";
import Header from "../../../components/header/Header";
import Button from "../../../components/button/Button";
import { SchoolGroup } from "../../../types";
import { gql } from "@apollo/client";
import Footer from "../../../components/footer/Footer";
import CollectionCard from "@src/pages/collection/CollectionCard";
import CollectionFilterLayout from "@src/components/collection/FilterLayout";
import RadioGroup from "@src/components/radio-group/RadioGroup";
import { useHistory } from "react-router-dom";
import Spinner from "@src/components/spinner/Spinner";
import { debounce } from "@src/utils/helpers";
import { logger } from "../../../utils/logger";
import FooterPage from "@src/components/footer-page/FooterPage";

type TableStates = {
  country: string;
  keyword: string;
  programId: string;
};

const defaultStates = {
  country: "",
  keyword: "",
  programId: "",
};

export const GroupsQuery = gql`
  query GetSpecialPrograms {
    listGroups {
      id
      name
    }
  }
`;

export type GroupsResponse = {
  listGroups: SchoolGroup[];
};

export const sortOptions = [
  { label: "Name", value: "0" },
  { label: "Country", value: "1" },
];

export const orderOptions = [
  { label: "Ascending", value: "0" },
  { label: "Descending", value: "1" },
];

function SuperCollections() {
  const history = useHistory();
  const [collectionItems, setCollectionItems] = useState<any[] | null>([]);
  const [getCollectionList, { data: collectionList, loading }] =
    useGetAllCollectionsLazyQuery({ fetchPolicy: "network-only" });
  const [order, setOrder] = useState("0");
  const [sortBy, setSortBy] = useState("0");
  const [states, setStates] = useState<TableStates>(defaultStates);

  const debouncedApiCall = useRef(
    debounce(
      (states: TableStates) =>
        getCollectionList({ variables: { input: states } }),
      500,
    ),
  ).current;

  // First Load
  useEffect(() => {
    debouncedApiCall(defaultStates);
  }, [debouncedApiCall]);

  useEffect(() => {
    if (collectionList && collectionList?.getAllCollections)
      setCollectionItems(collectionList?.getAllCollections);
  }, [collectionList]);

  useEffect(() => {
    let items;
    if (!collectionItems || collectionItems.length === 0) return;
    items = [...collectionItems]?.sort((a, b) => {
      if (sortBy === "0") {
        return order === "0"
          ? a?.name.localeCompare(b?.name)
          : b?.name.localeCompare(a?.name);
      }
      return order === "0"
        ? a?.country.localeCompare(b?.country)
        : b?.country.localeCompare(a?.country);
    });
    setCollectionItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, order]);

  const handleResetFilters = useCallback(() => {
    setStates(defaultStates);
    debouncedApiCall(defaultStates);
  }, [debouncedApiCall]);

  const handleStatesChange = useCallback(
    (key: string, value: any) => {
      setStates((prev) => {
        const newQuery = {
          ...prev,
          [key]: key === "country" ? value.value : value,
        };
        logger.info("new query", newQuery);
        debouncedApiCall(newQuery);

        return newQuery;
      });
    },
    [debouncedApiCall],
  );

  const onCreateCollection = () => {
    history.push(`/super/collections/edit/new`);
  };

  const onEditCollection = (id: string) => {
    history.push(`/super/collections/edit/${id}`);
  };

  return (
    <>
      <Header />
      <div className={styles.page}>
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>Collections Management</h2>
            </div>
            <div className={styles.rightHeader}>
              <div className={styles.search}></div>
              <Button
                onClick={onCreateCollection}
                className={styles.addBtn}
                label={"Create Collection"}
              />
            </div>
          </div>
          <div className={styles.content}>
            <CollectionFilterLayout
              keyword={states.keyword}
              country={states.country}
              ageGroup={states.programId}
              handleChangeAgeGroup={(v) => handleStatesChange("programId", v)}
              handleChangeSearch={(v) => handleStatesChange("keyword", v)}
              handleChangeCountry={(v) => handleStatesChange("country", v)}
              handleResetFilters={handleResetFilters}
            />
          </div>
          <div className={styles.collectionContainer}>
            <div className={styles.sortContainer}>
              <div className={styles.sortItemContainer}>
                <div className={styles.sortTitle}>Sort by:</div>
                <RadioGroup
                  value={sortBy}
                  options={sortOptions}
                  onChange={(value) => setSortBy(value)}
                  name={""}
                  hasError={false}
                  subTitle={""}
                  style={{ display: "flex", gap: "0.5rem" }}
                />
              </div>
              <div className={styles.sortItemContainer}>
                <div className={styles.sortTitle}>Order:</div>
                <RadioGroup
                  value={order}
                  options={orderOptions}
                  onChange={(value) => setOrder(value)}
                  name={""}
                  hasError={false}
                  subTitle={""}
                  style={{ display: "flex", gap: "0.5rem" }}
                />
              </div>
            </div>
            {loading ? (
              <div className={styles.loadingBar}>
                <Spinner width={30} height={30} />
              </div>
            ) : (
              <div className={styles.collectionCardsContainer}>
                {collectionItems?.map((collection, key) => {
                  return (
                    <CollectionCard
                      key={key}
                      collection={collection}
                      onEditCollection={onEditCollection}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <FooterPage />
      </div>
      {/* <Footer /> */}
    </>
  );
}

export default SuperCollections;
